const TREEMAP_COLOR_PALETTE = [
    '#4FA0FF',
    '#194C7A',
    '#FFB5B5',
    '#FF6779',
    '#FF9E55',
    '#E15E1F',
    '#0C2632',
    '#155777',
    '#69A0BB',
    '#99CDE6',
    '#0C42FF',
    '#3D96FF',
    '#A4D1FF',
    '#FFDBDB',
    '#FFB1B1',
    '#FF5468',
    '#FF8C55',
    '#FFC79C',
    '#CCEBBE',
    '#7ACE9A',
];

const formatTreemapValue = (value) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${Number.isInteger(value) ? value.toString() : value.toFixed(2)}`;
};

const colorAncestorId = (nodeId, coloringLevelIndex) => {
    try {
        const parts = JSON.parse(nodeId);
        return JSON.stringify(
            parts.slice(0, coloringLevelIndex + 1).map(String)
        );
    } catch {
        return nodeId;
    }
};

const colorTreemapPoints = (points, hierarchyLevels, colors) => {
    const coloringLevelIndex = Math.max(
        0,
        hierarchyLevels.findIndex(
            (level) => level.useForColoring || level.use_for_coloring
        )
    );

    return points.map((point) => {
        const ancestorId = colorAncestorId(point.id, coloringLevelIndex);
        if (!colors.has(ancestorId)) {
            colors.set(
                ancestorId,
                TREEMAP_COLOR_PALETTE[
                    colors.size % TREEMAP_COLOR_PALETTE.length
                ]
            );
        }
        return {
            ...point,
            color: point.color || colors.get(ancestorId),
            colorValue: point.value || 0,
        };
    });
};

export const buildTreemapChartOptions = (chartBlock, rows) => {
    const queries = chartBlock.propertySeriesQueries || [];
    // One colour map across every row group: ancestor ids carry the full
    // hierarchy path, so a per-group map would restart the palette and give
    // each series' first ancestor the same colour.
    const colors = new Map();
    const points = rows.flatMap((rowGroup, index) => {
        const query = queries[index] || {};
        const hierarchyLevels =
            query.hierarchyLevels || query.hierarchy_levels || [];
        return colorTreemapPoints(rowGroup, hierarchyLevels, colors);
    });

    return {
        title: { text: null },
        ...(chartBlock.exporting ? { exporting: chartBlock.exporting } : {}),
        ...(chartBlock.plotOptions
            ? { plotOptions: chartBlock.plotOptions }
            : {}),
        series: [
            {
                name: chartBlock.title || '',
                type: 'treemap',
                layoutAlgorithm: chartBlock.layoutAlgorithm || 'sliceAndDice',
                allowDrillToNode: true,
                animationLimit: 1000,
                dataLabels: { enabled: false },
                colorByPoint: false,
                tooltip: {
                    pointFormatter: function () {
                        return formatTreemapValue(this.value);
                    },
                },
                levels: [
                    {
                        level: 1,
                        dataLabels: {
                            enabled: true,
                            style: { fontWeight: 'normal' },
                            formatter: function () {
                                return `${this.point.name}<br/>${formatTreemapValue(
                                    this.point.value
                                )}`;
                            },
                        },
                        borderWidth: chartBlock.borderWidth ?? 3,
                        borderColor: chartBlock.borderColor || '#fff',
                        levelIsConstant: false,
                    },
                ],
                accessibility: { exposeAsGroupOnly: true },
                breadcrumbs: {
                    buttonTheme: {
                        style: { color: '#333' },
                        states: { select: { style: { color: '#333' } } },
                    },
                },
                data: points,
            },
        ],
    };
};
